import { memo, useEffect, useRef } from 'react'
import { VirtualizedList, type VirtualizedListRef } from '@eo/flatlist'
import Thumbnail from './Thumbnail'
import { IslandLabel } from './IslandLabel'
import { ScrollingEllipsisText } from './ScrollingEllipsisText'
import { ThumbFocusMask } from './ThumbFocusMask'
import { useFeatureFlag } from '@/hooks/useFeatureFlags'
import { THUMB_BADGE_ISLAND, THUMB_BADGE_REST } from './islandPresets'
import type { MediaClass } from '@/types/api'
import type { ImageSource } from '@/types/openseadragon'
import type { ShapeThumb } from '@/types/annotations'
import { BoxTypeFilter, type BoxKind } from './AnnotationTypeFilter'

interface MemoizedThumbnailProps {
  item: MediaClass
  src: string
}

const MemoizedThumbnail = memo(
  function MemoizedThumbnail({ item, src }: MemoizedThumbnailProps) {
    const bounds = { x1: item.bbox_x1, y1: item.bbox_y1, x2: item.bbox_x2, y2: item.bbox_y2 }
    return (
      <Thumbnail
        src={src}
        width={156}
        height={156}
        initialBounds={bounds}
        style={{
          display: 'flex',
          overflow: 'hidden',
          alignItems: 'center',
        }}
      />
    )
  },
  (prev, next) => prev.item.id === next.item.id && prev.src === next.src
)

// Selection = blue everywhere (lab variant B, chosen 2026-09-18): matches the
// media-list selection language and never collides with a category color. The
// in-thumb box border stays the category's own color/style — identity and
// selection are separate channels.
const SELECTION_GLOW = 'var(--accent-blue)'

// rc-app parity: the box occupies at most 75% of the thumb; the crop shows
// surrounding context and the bbox is drawn inside the thumbnail.
export const OVERLAY_FIT_FACTOR = 0.75

/** The bbox's size in a pixel-true frame: normalized y spans H, normalized x spans
 *  W, so y must be scaled by aspect (H/W) before comparing with x. Square-in-
 *  normalized-units was square-in-pixels only on near-square slides (E031);
 *  IGR's 2:1 slides drew square boxes as tall rectangles. */
export function thumbBoxSize(b: ShapeThumb['bounds'], aspect: number): { w: number; h: number } {
  return { w: b.x2 - b.x1, h: (b.y2 - b.y1) * aspect }
}

/** Pixel-square view bounds around the bbox center sized so the box fills ≤75%. */
export function thumbViewBounds(b: ShapeThumb['bounds'], aspect = 1): ShapeThumb['bounds'] {
  const cx = (b.x1 + b.x2) / 2
  const cy = (b.y1 + b.y2) / 2
  const { w, h } = thumbBoxSize(b, aspect)
  const half = Math.max(w, h) / OVERLAY_FIT_FACTOR / 2
  return { x1: cx - half, y1: cy - half / aspect, x2: cx + half, y2: cy + half / aspect }
}

// In-thumb bbox border colors — match the main-viewer overlay styles in index.css.
const THUMB_BOX_COLOR: Record<ShapeThumb['type'], string> = {
  gt: '#37c759',
  rejection: '#fe3a30',
  suggestion: '#1c1c1e',
  roi: '#F1A33C',
  circle: '#68CE67',
  point: '#067AFF',
}

interface MemoizedShapeThumbProps {
  item: ShapeThumb
  src: string
  isSelected?: boolean
  /** Account flag: flood outside the box in the category colour instead of outlining it */
  focusMask?: boolean
  /** Image height / width */
  aspect: number
}

const MemoizedShapeThumb = memo(
  function MemoizedShapeThumb({ item, src, isSelected, focusMask, aspect }: MemoizedShapeThumbProps) {
    // Crop shows the box + surrounding context (≤75% fill); the bbox itself is
    // drawn inside the thumb. The view bounds are square, so the on-screen box
    // size is the bbox scaled by 156·0.75/max(w,h), centered.
    const { w, h } = thumbBoxSize(item.bounds, aspect)
    const scale = (156 * OVERLAY_FIT_FACTOR) / Math.max(w, h)
    const boxW = w * scale
    const boxH = h * scale
    return (
      <div
        className="relative h-[156px] w-[156px] overflow-hidden rounded-md"
        style={{ boxShadow: `inset 0 0 0 2px ${item.color}` }}
        data-thumb-item-id={item.id}
        data-thumb-bounds={import.meta.env.DEV ? JSON.stringify(item.bounds) : undefined}
      >
        <Thumbnail
          src={src}
          width={156}
          height={156}
          initialBounds={thumbViewBounds(item.bounds, aspect)}
          style={{ display: 'flex', overflow: 'hidden', alignItems: 'center' }}
        />
        {/* The bbox drawn inside the thumb (rc-app OverlayBox): category color,
            dashed for suggestions — or, with the focus mask on, the colour floods
            everything outside the box (yellow when selected). */}
        {focusMask ? (
          <ThumbFocusMask
            box={{ left: (156 - boxW) / 2, top: (156 - boxH) / 2, width: boxW, height: boxH }}
            kind={item.type}
            selected={isSelected}
          />
        ) : (
          <div
            className="pointer-events-none absolute rounded-[2px] transition-colors duration-[400ms]"
            style={{
              left: (156 - boxW) / 2,
              top: (156 - boxH) / 2,
              width: boxW,
              height: boxH,
              border: `2px ${item.type === 'suggestion' ? 'dashed' : 'solid'} ${THUMB_BOX_COLOR[item.type]}`,
              boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.35)',
            }}
          />
        )}
        {/* No text label — the in-thumb box border color/style does the talking
            (green GT / red rejected / dashed suggestion). */}
        {/* rc-app parity: confidence rank bottom-left, score bottom-right */}
        {/* Each number grows Dynamic-Island style on its OWN hover — 10px is
            fine for scanning, the enlarged pill is for actually reading one. */}
        {item.rank != null && (
          <IslandLabel
            {...THUMB_BADGE_ISLAND}
            text={String(item.rank)}
            align="left"
            restClassName={`${THUMB_BADGE_REST} font-semibold`}
            expandedClassName="font-mono font-semibold"
            className="bottom-1 left-1"
          />
        )}
        {item.score != null && (
          <IslandLabel
            {...THUMB_BADGE_ISLAND}
            text={item.score.toFixed(2)}
            align="right"
            restClassName={`${THUMB_BADGE_REST} font-medium`}
            expandedClassName="font-mono font-medium"
            className="bottom-1 right-1"
          />
        )}
        {/* Selection: glowing outline (rc-app's "lined box" mode) in the
            category's color — amber for suggestions (rc-app convention), green
            for ground truth, red for rejected. Rendered as an overlay above the
            viewer canvas (inset shadows on the wrapper would be hidden beneath
            it), with rc-app OverlayBox's 400ms fade. */}
        <div
          className="pointer-events-none absolute inset-0 rounded-md transition-opacity duration-[400ms]"
          style={{
            opacity: isSelected && !focusMask ? 1 : 0,
            boxShadow: `inset 0 0 0 2px ${SELECTION_GLOW}, inset 0 0 14px color-mix(in srgb, ${SELECTION_GLOW} 60%, transparent)`,
          }}
        />
      </div>
    )
  },
  (prev, next) =>
    prev.item.id === next.item.id &&
    prev.src === next.src &&
    prev.item.rank === next.item.rank &&
    prev.isSelected === next.isSelected &&
    prev.focusMask === next.focusMask &&
    prev.aspect === next.aspect,
)

function ThumbnailPlaceholder() {
  return (
    <div data-thumbnail-wrapper="true" data-is-scrolling="true">
      <div className="relative h-[156px] w-[156px] overflow-hidden rounded-md">
        <div className="chrome-panel absolute inset-0 rounded-md border border-border-default/40" />
      </div>
    </div>
  )
}

interface FloatingRightRailProps {
  navigatorWidth: number
  navigatorHeight: number
  annotations: MediaClass[]
  imageSources: ImageSource[]
  organizationId: string
  isDrawingMode: boolean
  className?: string
  /** Image height / width — the in-thumb crop and box are drawn pixel-true */
  imageAspect?: number
  onAnnotationClick?: (annotation: MediaClass) => void
  // Bbox-semantics navigator (takes precedence over `annotations` when present)
  shapeItems?: ShapeThumb[]
  // Currently selected item (amber highlight, rc-app parity)
  selectedItemId?: string | null
  // Bbox-semantics filter (ground truth / rejections / suggestions)
  boxFilter?: {
    visible: Record<BoxKind, boolean>
    counts: Record<BoxKind, number>
    onToggle: (kind: BoxKind) => void
  }
  // Suggestion score-floor slider (display filter only — nothing is discarded)
  scoreFilter?: {
    floor: number
    shown: number
    total: number
    onChange: (floor: number) => void
  }
  onShapeClick?: (item: ShapeThumb) => void
}

export function FloatingRightRail({
  navigatorWidth,
  navigatorHeight,
  annotations,
  imageSources,
  organizationId,
  isDrawingMode,
  className,
  imageAspect = 1,
  onAnnotationClick,
  shapeItems,
  selectedItemId,
  boxFilter,
  scoreFilter,
  onShapeClick,
}: FloatingRightRailProps) {
  // Scroll the selected thumb into view (centered) when selection arrives from
  // outside the rail — deep links, canvas clicks, accept/reject auto-advance.
  // A click inside the rail suppresses the scroll briefly so the clicked thumb
  // doesn't jump under the cursor (rc-app's userSelectedRef pattern).
  const focusMask = useFeatureFlag('useCropMask')
  const railListRef = useRef<VirtualizedListRef>(null)
  const lastUserPressRef = useRef(0)
  const lastScrolledSelRef = useRef<string | null>(null)
  const selectedIndex =
    shapeItems && selectedItemId != null
      ? shapeItems.findIndex((s) => s.id === selectedItemId)
      : -1
  useEffect(() => {
    if (selectedItemId == null || selectedIndex < 0) return
    if (lastScrolledSelRef.current === selectedItemId) return
    lastScrolledSelRef.current = selectedItemId
    if (Date.now() - lastUserPressRef.current < 600) return
    railListRef.current?.scrollToIndex(selectedIndex, 'center')
  }, [selectedItemId, selectedIndex])

  if (!imageSources?.[0] || !organizationId) {
    return null
  }

  const navigatorPanelHeight = navigatorHeight + 18
  const shapeMode = !!boxFilter
  const hasAnnotations = annotations && annotations.length > 0

  // Class name heads the thumbnail panel — mirrors the left rail's skill-name header
  const classHeader = className && (
    <div className="shrink-0 border-b border-border-default/40 px-3 py-2">
      <h2 className="text-h3 capitalize text-text-primary">
        <ScrollingEllipsisText text={className} />
      </h2>
    </div>
  )

  return (
    <aside
      className="absolute z-40 right-4 flex w-[174px] flex-col gap-2"
      style={{
        top: 'calc(var(--header-h, 80px) + 1rem)',
        bottom: '1rem',
        opacity: isDrawingMode ? 0 : 1,
        transform: isDrawingMode ? 'translateX(100%)' : 'translateX(0)',
        pointerEvents: isDrawingMode ? 'none' : 'auto',
        transition: 'opacity 300ms ease-in-out, transform 300ms ease-in-out, top 300ms ease-in-out',
      }}
    >
      {/* Navigator (minimap) */}
      <div
        className="chrome-panel box-border flex shrink-0 items-center justify-center overflow-hidden rounded-modal border border-border-default/40 p-2"
        style={{ height: `${navigatorPanelHeight}px` }}
      >
        <div
          id="osd-navigator-internal"
          style={{
            width: `${navigatorWidth}px`,
            height: `${navigatorHeight}px`,
            borderRadius: '0.5rem',
            overflow: 'hidden',
          }}
        />
      </div>

      {/* Category filter — its own small panel under the navigator (where the class
          label used to sit; the label is now the thumbnail panel's header). */}
      {boxFilter && (
        <div className="chrome-panel box-border shrink-0 rounded-modal border border-border-default/40 p-2">
          <BoxTypeFilter
            visible={boxFilter.visible}
            counts={boxFilter.counts}
            onToggle={boxFilter.onToggle}
          />
        </div>
      )}

      {/* Nothing to list yet (no boxes, no annotations): the class name still shows, alone */}
      {!shapeMode && !hasAnnotations && classHeader && (
        <div className="chrome-panel box-border shrink-0 rounded-modal border border-border-default/40 [&>div]:border-b-0">
          {classHeader}
        </div>
      )}

      {/* VSI shape navigator — filter + clickable thumbnails (fills remaining space) */}
      {shapeMode && (
        <div className="chrome-panel box-border flex min-h-0 flex-1 flex-col overflow-hidden rounded-modal border border-border-default/40">
          {classHeader}
          <div className="rail-list min-h-0 flex-1 p-2">
            {shapeItems && shapeItems.length > 0 ? (
              <VirtualizedList<ShapeThumb>
                ref={railListRef}
                data={shapeItems}
                itemSize={156}
                height="100%"
                rowGap={8}
                hideScrollbar={true}
                enableScrollingState={true}
                selectedIndices={
                  selectedItemId != null
                    ? shapeItems.flatMap((s, i) => (s.id === selectedItemId ? [i] : []))
                    : []
                }
                selectedItemClassName=""
                renderItem={({ item, style, isScrolling, isSelected }) => (
                  <div style={{ ...style, cursor: 'pointer' }}>
                    {isScrolling ? (
                      <ThumbnailPlaceholder />
                    ) : (
                      <MemoizedShapeThumb
                        item={item}
                        src={imageSources[0].url}
                        isSelected={isSelected}
                        focusMask={focusMask}
                        aspect={imageAspect}
                      />
                    )}
                  </div>
                )}
                keyExtractor={(item) => item.id}
                onItemPress={(item) => {
                  lastUserPressRef.current = Date.now()
                  onShapeClick?.(item)
                }}
              />
            ) : (
              <div className="flex h-full items-center justify-center px-2 text-center text-body-small text-text-secondary">
                No annotations match the current filter.
              </div>
            )}
          </div>
          {/* Suggestion score floor — a display filter, not a data cap. Drag
              left to reveal the model's lower-confidence calls. */}
          {scoreFilter && boxFilter?.visible.suggestion && (
            <div className="shrink-0 border-t border-border-default/40 px-3 pb-2 pt-1.5">
              {/* Numbers in mono (like the thumb badges) so the digits hold still and read cleanly */}
              <div className="flex items-baseline justify-between text-label-small text-text-secondary">
                <span>
                  Score ≥{' '}
                  <span className="font-mono text-[12px] text-text-primary">
                    {scoreFilter.floor.toFixed(2)}
                  </span>
                </span>
                <span>
                  <span className="font-mono text-[12px] text-text-primary">{scoreFilter.shown}</span> of{' '}
                  <span className="font-mono text-[12px] text-text-primary">{scoreFilter.total}</span>
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={scoreFilter.floor}
                onChange={(e) => scoreFilter.onChange(Number(e.target.value))}
                aria-label="Minimum suggestion score"
                className="rail-slider mt-1"
                style={{ '--pct': `${scoreFilter.floor * 100}%` } as React.CSSProperties}
              />
            </div>
          )}
        </div>
      )}

      {/* Annotation thumbnails (MediaClass) — fills remaining vertical space */}
      {!shapeMode && hasAnnotations && (
        <div className="chrome-panel box-border flex min-h-0 flex-1 flex-col overflow-hidden rounded-modal border border-border-default/40">
          {classHeader}
          <div className="rail-list min-h-0 flex-1 p-2">
            <VirtualizedList<MediaClass>
              data={annotations}
              itemSize={156}
              height="100%"
              rowGap={8}
              hideScrollbar={true}
              enableScrollingState={true}
              renderItem={({ item, style, isScrolling }) => (
                <div style={{ ...style, cursor: 'pointer' }}>
                  {isScrolling ? (
                    <ThumbnailPlaceholder />
                  ) : (
                    <MemoizedThumbnail item={item} src={imageSources[0].url} />
                  )}
                </div>
              )}
              keyExtractor={(item) => item.id}
              onItemPress={(item) => onAnnotationClick?.(item)}
            />
          </div>
        </div>
      )}
    </aside>
  )
}
